// Implements: F-UIX-020, F-UIX-007
/**
 * One colour of a palette (the mockup's `.color-row`): a native colour picker beside the colour's
 * hexadecimal text. The text is the value: it keeps the server's `#rgb|#rrggbb` grammar and what the
 * person typed (a half-typed colour stays visible and fails the contrast list); the picker only
 * shows it in six digits, black while the text is not a colour, and writes six digits back.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field } from "../../../shared/ui/molecules/Field";
import { pickerValue } from "./previewTokens";
import styles from "./ColourField.module.css";

export interface ColourFieldProps {
  readonly id: string;
  /** The role's name (Primario, Fondo, …): it names the text field, and the picker with it. */
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean | undefined;
}

export function ColourField({ id, label, value, onChange, disabled = false }: ColourFieldProps): ReactElement {
  const { t } = useTranslation();
  return (
    <Field id={id} label={label}>
      <div className={styles.row}>
        <input
          className={styles.picker}
          type="color"
          aria-label={t("design.editor.pickerFor", { role: label })}
          value={pickerValue(value)}
          disabled={disabled}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
        <Input id={id} value={value} onChange={onChange} disabled={disabled} />
      </div>
    </Field>
  );
}
